import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import InputField from "components/InputField";
import { AdditionalOptionsListbox } from "components/hosted-scraping/edit-project-components/AdditionalOptionsListBox";
import { cx } from "utils";
import ToggleSwitch from "./ToggleSwitch";
import { Tooltip } from "components/Tooltip";
import { canUsePremium, canUseUltraPremium } from "utils/planUtils";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


// Restricted country codes for crawler
const restrictedCountryCodes = [
  { text: 'None', value: undefined },
  { text: 'US', value: 'US' },
  { text: 'EU', value: 'EU' },
];

const deviceTypes = [
  { text: 'None', value: undefined },
  { text: 'Desktop', value: 'Desktop' },
  { text: 'Mobile', value: 'Mobile' },
];

const AdvancedOptionsDisclosure = ({
  watchedValues,
  setValue,
}: any) => {
  const { activeSubscription } = useActiveSubscription();

  return (
    <Disclosure>
      {({ open }) => (
        <>
          <DisclosureButton
            className={cx(
              "flex flex-row items-center w-full transition-colors duration-50 p-4"
            )}
          >
            {open ? (
              <ChevronUpIcon className="h-4 w-4 mr-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 mr-4" />
            )}
            Advanced options
          </DisclosureButton>

          <DisclosurePanel className="flex flex-col p-4" style={{ marginTop: 0 }}>
          {/* Toggle Options */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4 mb-6">
            <ToggleSwitch
              name="disableRedirects"
              label="Disable follow redirects"
              content="Prevent the crawler from automatically following HTTP redirects."
              watchedValues={watchedValues}
              setValue={setValue}
              className="flex-shrink-0"
            />
            <ToggleSwitch
              name="retry404"
              label="Retry 404 responses"
              content="Automatically retry URLs that return 404 errors."
              watchedValues={watchedValues}
              setValue={setValue}
              className="flex-shrink-0"
            />
            <ToggleSwitch
              name="renderJs"
              label="Render JavaScript"
              content="Execute JavaScript on pages using a headless browser. Required for dynamic content."
              watchedValues={watchedValues}
              setValue={setValue}
              className="flex-shrink-0"
            />
            <ToggleSwitch
              name="screenshot"
              label="Generate screenshot"
              content="Capture a PNG of each crawled page. Requires page rendering, so each page is charged as a rendered request. The image URL is in the sa-screenshot header of each page's result."
              watchedValues={watchedValues}
              setValue={setValue}
              className="flex-shrink-0"
            />
            <ToggleSwitch
              name="useOwnHeaders"
              label="Use own headers"
              content="Use your own custom headers instead of default browser headers."
              watchedValues={watchedValues}
              setValue={setValue}
              className="flex-shrink-0"
            />
            {canUseUltraPremium(activeSubscription?.planSlug) && (
              <ToggleSwitch
                name="advancedBypassing"
                label="Advanced bypassing"
                content="Advanced bypass mechanisms for difficult domains. Costs 30 API credits per request."
                watchedValues={watchedValues}
                setValue={setValue}
                className="flex-shrink-0"
              />
            )}
            {canUsePremium(activeSubscription?.planSlug) && (
              <ToggleSwitch
                name="residentialIps"
                label="Residential and mobile IPs"
                content="Use premium residential and mobile IP addresses. Costs 10 API credits per request."
                watchedValues={watchedValues}
                setValue={setValue}
                className="flex-shrink-0"
              />
            )}
          </div>

          {/* Additional Fields */}
          <div className="grid grid-cols-1 xl:grid-cols-3 sm:grid-cols-2 xs:grid-cols-1 gap-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Tooltip content="Reuse the same proxy by setting a session number. Useful for maintaining consistent IP addresses across requests.">Session number</Tooltip>
              </label>
              <InputField
                name="sessionNumber"
                type="number"
                placeholder="e.g. 1"
                value={watchedValues.sessionNumber ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || value === null) {
                    setValue("sessionNumber", undefined);
                  } else {
                    const parsed = parseInt(value, 10);
                    setValue("sessionNumber", isNaN(parsed) ? undefined : parsed);
                  }
                }}
              />
            </div>

            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Tooltip content="Use country-specific proxies for geo-targeting. Crawler supports US and EU regions.">Country code</Tooltip>
              </label>
              <div className="relative">
                <AdditionalOptionsListbox
                  value={watchedValues.countryCode}
                  options={restrictedCountryCodes}
                  callback={(value) => setValue("countryCode", value)}
                  shortList
                  buttonTestId="crawler-country-code"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Tooltip content="Simulate different device types (Desktop, Mobile). Will be overridden if you use custom headers.">Device type</Tooltip>
              </label>
              <div className="relative">
                <AdditionalOptionsListbox
                  value={watchedValues.deviceType}
                  options={deviceTypes}
                  callback={(value) => setValue("deviceType", value)}
                  shortList
                  buttonTestId="crawler-device-type"
                />
              </div>
            </div>
          </div>
        </DisclosurePanel>
      </>
      )}
    </Disclosure>
  );
};

export default AdvancedOptionsDisclosure;
